// ค้างชำระ — the chase list. One row per patient who still owes the clinic.
//
// THE MODEL (owner-confirmed 2026-08-13). A debt is a NEGATIVE `priceXunit` on a
// billing line of type ค้างชำระ; a repayment is POSITIVE. So a patient's balance
// is −(Σ priceXunit) and they only appear here while that is positive. Settled
// patients drop off by construction.
//
// ⚠️ This is money the PATIENT owes the clinic — distinct from ค้างรับ on
// /reports/reimbursement, which is money a PAYER (ปกส./ประกัน) owes us. Two
// different debts owed by two different parties; the wording must never blur
// them, or someone quotes the wrong figure at the counter.
//
// The fold happens in Xano (~1,700 lines / ~800 patients at รัชโยธิน alone), so
// this page only ever receives one row per debtor.

import { useEffect, useMemo, useState } from 'react'
import { AlertTriangle, Landmark, Search, X, Loader2 } from 'lucide-react'
import { apiGet, cachedGet } from '../../lib/api.js'
import { asList } from '../../lib/chart-helpers.js'
import { fmtBill as fmt } from '../../lib/billing.js'
import { fmtDate } from '../../lib/datetime.js'
import { usePersistedState } from '../../lib/persist.js'
import { useMe } from '../../lib/me.js'
import { STATUS_TONES, statusTone } from '../../data/patientStatuses.js'
import OutstandingDrawer from '../../components/reports/OutstandingDrawer.jsx'

const relabelBranch = (name) => (/สนามเป้า/.test(name || '') ? 'อารีย์ (เดิมสนามเป้า)' : name)

const INPUT = 'rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[13px] text-slate-700 focus:outline-none focus:ring-2 focus:ring-brand-100 focus:border-brand-300'

// The query behind this page is EXPENSIVE — a whole-clinic scan runs for tens of
// seconds. Firing it per keystroke stacked a dozen concurrent scans on Xano and
// pushed each one from ~20s to ~90s, so the page never settled and read as
// "the report returns nothing". Wait for typing to stop before asking.
function useDebounced(value, ms) {
  const [v, setV] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms)
    return () => clearTimeout(t)
  }, [value, ms])
  return v
}

// Seconds since `active` went true. A whole-clinic scan runs ~8-10s; a spinner
// that never changes is indistinguishable from a hang — which is exactly how the
// old bare skeleton read. A ticking number proves the page is still alive.
function useElapsed(active) {
  const [secs, setSecs] = useState(0)
  useEffect(() => {
    setSecs(0)
    if (!active) return
    const t0 = Date.now()
    const id = setInterval(() => setSecs(Math.floor((Date.now() - t0) / 1000)), 250)
    return () => clearInterval(id)
  }, [active])
  return secs
}

export default function Outstanding() {
  const me = useMe()
  // `null` = the user has never picked a branch, which is DISTINCT from '' =
  // they deliberately chose ทุกสาขา. Once /__app/me lands we settle the null into
  // their own branch: one branch scans ~1,700 lines (fast), ทุกสาขา scans ~11,900
  // (slow), and front-desk staff chase their own branch's patients anyway.
  // Key is `owe:branch2` so anyone carrying the old '' default gets the new one.
  const [branch, setBranch] = usePersistedState('owe:branch2', null)
  const [minBalance, setMinBalance] = usePersistedState('owe:min', '1000')
  const [branches, setBranches] = useState([])
  const [data, setData] = useState(null)
  const [err, setErr] = useState('')
  const [loading, setLoading] = useState(true)
  const [q, setQ] = useState('')
  const [open, setOpen] = useState(null) // the row whose drawer is showing

  useEffect(() => {
    if (branch === null && me) setBranch(String(me.branch_id || ''))
  }, [me, branch, setBranch])

  // Negative/blank minimums are meaningless here and only widen the scan to every
  // debtor in the clinic — the heaviest query there is. Clamp before asking.
  const debouncedMin = useDebounced(minBalance, 400)
  const minParam = useMemo(() => {
    const n = Number(debouncedMin)
    if (!Number.isFinite(n) || n < 0) return 1
    return n
  }, [debouncedMin])

  useEffect(() => {
    cachedGet('/api/branches').then((r) => setBranches(asList(r))).catch(() => {})
  }, [])

  useEffect(() => {
    if (branch === null) return // wait for the default to settle; don't fire twice
    const ctrl = new AbortController()
    setLoading(true); setErr('')
    apiGet('/api/billing-outstanding',
      { branch_id: branch || undefined, min_balance: minParam },
      { signal: ctrl.signal })
      .then((r) => { setData(r); setLoading(false) })
      .catch((e) => {
        if (e?.name === 'AbortError') return // superseded — the newer request owns the UI
        setErr(e?.message || 'โหลดข้อมูลไม่สำเร็จ'); setData(null); setLoading(false)
      })
    // Cancelling matters more than ignoring: it frees the Xano worker instead of
    // leaving a superseded whole-clinic scan running to completion.
    return () => ctrl.abort()
  }, [branch, minParam])

  // The endpoint returns ascending by amount; a chase list wants the biggest
  // debt first.
  const rows = useMemo(() => {
    const items = asList(data?.items)
    const sorted = [...items].sort((a, b) => Number(b.outstanding || 0) - Number(a.outstanding || 0))
    const needle = q.trim().toLowerCase()
    if (!needle) return sorted
    return sorted.filter((r) =>
      String(r.fullname || '').toLowerCase().includes(needle) ||
      String(r.HN || '').toLowerCase().includes(needle))
  }, [data, q])

  const elapsed = useElapsed(loading)
  const allBranches = !branch

  const shownTotal = useMemo(() => rows.reduce((s, r) => s + Number(r.outstanding || 0), 0), [rows])

  // id → label, so the drawer can name the branch each line was incurred at
  // without fetching /api/branches a second time.
  const branchName = useMemo(() => Object.fromEntries(
    branches.map((b) => [String(b.id), relabelBranch(b.name || b.branch_name || `สาขา #${b.id}`)])
  ), [branches])

  return (
    <div className="px-10 py-8 max-w-[1100px]">
      <h1 className="text-[22px] font-semibold text-slate-900 tracking-tight mb-1">ค้างชำระ</h1>
      <p className="text-[14px] text-slate-500 mb-6">
        คนไข้ที่ยังค้างชำระเงินกับคลินิก · เรียงจากยอดมากไปน้อย
      </p>

      <div className="flex items-center gap-2 flex-wrap mb-5">
        {/* `?? ''` — branch is null until /__app/me settles the default; a null
            value would make React treat the select as uncontrolled for a frame. */}
        <select className={INPUT} value={branch ?? ''} onChange={(e) => setBranch(e.target.value)}>
          <option value="">ทุกสาขา</option>
          {branches.map((b) => (
            <option key={b.id} value={b.id}>{relabelBranch(b.name || b.branch_name || `สาขา #${b.id}`)}</option>
          ))}
        </select>

        <label className="inline-flex items-center gap-1.5 text-[13px] text-slate-600">
          ยอดขั้นต่ำ
          <input className={`${INPUT} w-24`} type="number" min="0" value={minBalance}
            onChange={(e) => setMinBalance(e.target.value)} />
          บาท
        </label>

        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input className={`${INPUT} pl-8 w-56`} placeholder="ค้นหาชื่อ หรือ HN"
            value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        {q && (
          <button onClick={() => setQ('')} className="text-[12.5px] text-slate-400 hover:text-slate-700 inline-flex items-center gap-1">
            <X size={12} /> ล้าง
          </button>
        )}

        {/* Re-filtering keeps the previous result on screen and says so, rather
            than blanking back to a skeleton — the numbers you were reading stay
            readable while the new ones are fetched. */}
        {loading && data && (
          <span className="inline-flex items-center gap-1.5 text-[12.5px] text-slate-500 ml-auto">
            <Loader2 size={13} className="animate-spin text-brand-500" />
            กำลังอัปเดต…{elapsed >= 3 ? ` ${elapsed} วิ` : ''}
          </span>
        )}
      </div>

      {err && (
        <div className="rounded-xl bg-rose-50 border border-rose-200 p-3 text-rose-700 text-[14px] flex items-start gap-2 mb-4">
          <AlertTriangle size={14} className="flex-none mt-0.5" /><div>{err}</div>
        </div>
      )}

      {/* FIRST load only — there is nothing on screen yet, so say what is happening
          and roughly how long it takes. The old version here was an unlabelled
          grey pulsing box, which during a 20s+ ทุกสาขา scan read as "this report
          is broken" rather than "this report is working". */}
      {loading && !data && !err && (
        <div className="rounded-2xl border border-slate-200 bg-white p-10 flex flex-col items-center justify-center gap-3" aria-busy="true">
          <Loader2 size={22} className="animate-spin text-brand-500" />
          <div className="text-[14px] text-slate-600">กำลังโหลดรายการค้างชำระ…</div>
          <div className="text-[12.5px] text-slate-400 text-center">
            {allBranches ? 'รวมข้อมูลทุกสาขา · ปกติใช้เวลาราว 10 วินาที' : 'กำลังรวมยอดค้างชำระของสาขานี้'}
            {elapsed >= 3 && <> · {elapsed} วินาที</>}
          </div>
        </div>
      )}

      {/* Dimmed, not replaced, while a re-filter is in flight — see the pill above.
          Rows stay clickable: the drawer fetches its own data, so opening one
          mid-refresh is still correct. */}
      {!err && data && (
        <div className={loading ? 'opacity-45 transition-opacity duration-200' : 'transition-opacity duration-200'}>
          <div className="mb-4 rounded-2xl border border-slate-100 bg-white p-4 flex flex-wrap gap-x-10 gap-y-3">
            <Stat label="คนไข้ที่ค้างชำระ" value={`${rows.length} คน`} />
            <Stat label="ยอดค้างรวม" value={`${fmt(shownTotal)} บาท`} strong />
            <Stat label="รายการที่ตรวจ" value={`${fmt(data.scanned || 0)} รายการ`} muted />
          </div>

          {rows.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-white/60 p-12 text-center text-[14px] text-slate-500">
              ไม่มีคนไข้ค้างชำระตามเงื่อนไขนี้
            </div>
          ) : (
            <div className="rounded-2xl border border-slate-100 bg-white overflow-hidden">
              <table className="w-full text-[13.5px]">
                <thead>
                  <tr className="text-[11.5px] uppercase tracking-wide text-slate-400 bg-slate-50/70">
                    <th className="text-left font-medium py-2 pl-4">คนไข้</th>
                    <th className="text-left font-medium py-2 px-2">HN</th>
                    <th className="text-left font-medium py-2 px-2">สถานะ</th>
                    <th className="text-left font-medium py-2 px-2">รักษาล่าสุด</th>
                    <th className="text-right font-medium py-2 px-2">รายการ</th>
                    <th className="text-right font-medium py-2 pr-4">ยอดค้างชำระ</th>
                  </tr>
                </thead>
                <tbody>
                  {/* The whole row opens the ledger drawer. It used to carry a
                      link straight out to the patient chart, which took staff off
                      the worklist to answer "what is this debt for" — that link
                      now lives inside the drawer, after the answer. */}
                  {rows.map((r) => {
                    const tone = STATUS_TONES[statusTone(String(r.status || '').trim())] || STATUS_TONES.none
                    return (
                      <tr
                        key={r.patient_id}
                        onClick={() => setOpen(r)}
                        className="border-t border-slate-50 hover:bg-slate-50/60 cursor-pointer"
                        title="ดูรายการค้างชำระทั้งหมด"
                      >
                        <td className="py-2 pl-4 text-slate-800">{r.fullname || '—'}</td>
                        <td className="px-2 font-mono text-[12.5px] text-slate-500">{r.HN || '—'}</td>
                        <td className="px-2">
                          {String(r.status || '').trim() ? (
                            <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10.5px] ring-1 ${tone.pill}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${tone.dot}`} />{r.status}
                            </span>
                          ) : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="px-2 text-[12.5px] text-slate-500 tabular-nums whitespace-nowrap">
                          {r.last_treatment ? fmtDate(r.last_treatment) : '—'}
                        </td>
                        <td className="px-2 text-right tabular-nums text-slate-400">{r.lines}</td>
                        <td className="pr-4 text-right tabular-nums font-bold text-slate-900">{fmt(r.outstanding)}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}

          <p className="mt-4 text-[12.5px] text-slate-400 flex items-start gap-1.5">
            <Landmark size={13} className="flex-none mt-0.5" />
            เป็นยอดที่ <strong className="font-semibold text-slate-500">คนไข้</strong> ค้างคลินิก —
            คนละส่วนกับ “ค้างรับ” ในรายงานรายการตามเบิก ซึ่งเป็นยอดที่ผู้จ่าย (ปกส./ประกัน) ค้างคลินิก
          </p>
        </div>
      )}

      {open && <OutstandingDrawer row={open} branchName={branchName} onClose={() => setOpen(null)} />}
    </div>
  )
}

function Stat({ label, value, strong, muted }) {
  return (
    <div>
      <div className="text-[12px] text-slate-400 mb-0.5">{label}</div>
      <div className={`tabular-nums ${strong ? 'text-[19px] font-bold text-slate-900' : muted ? 'text-[14px] text-slate-400' : 'text-[16px] font-semibold text-slate-700'}`}>
        {value}
      </div>
    </div>
  )
}
